import { Container, Grid, Box, Typography, Divider } from "@mui/material";
import {
  Button,
  Card,
  StatusBadge,
  CustomerTypeBadge,
  InputField,
  StatCard,
  LoadingSpinner,
  EmptyState,
  ErrorMessage,
} from "@/components/ui";
import {
  Email,
  Lock,
  Receipt,
  HourglassTop,
  LocalShipping,
  Euro,
} from "@mui/icons-material";

/**
 * Page de démonstration des composants UI réutilisables
 */
export function ComponentsDemo(): JSX.Element {
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h1" gutterBottom>
        Composants UI - Démo
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Démonstration de tous les composants UI réutilisables
      </Typography>

      {/* Buttons */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Buttons
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="text">Text</Button>
          <Button variant="danger">Danger</Button>
          <Button variant="primary" size="large">
            Large
          </Button>
          <Button variant="primary" size="small">
            Small
          </Button>
          <Button variant="primary" loading>
            Loading
          </Button>
          <Button variant="primary" disabled>
            Disabled
          </Button>
        </Box>
      </Box>

      {/* Cards */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Cards
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card title="Carte Simple" subtitle="Avec titre et sous-titre">
              <Typography>Contenu de la carte</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card
              title="Carte avec Actions"
              actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="text" size="small">
                    Annuler
                  </Button>
                  <Button variant="primary" size="small">
                    Valider
                  </Button>
                </Box>
              }
            >
              <Typography>Contenu avec actions en bas</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card padding="large">
              <Typography>Padding large</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card padding="small">
              <Typography>Padding small</Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Status Badges */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Status Badges
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <StatusBadge status="pending" />
          <StatusBadge status="confirmed" />
          <StatusBadge status="inProgress" />
          <StatusBadge status="ready" />
          <StatusBadge status="inDelivery" />
          <StatusBadge status="delivered" />
          <StatusBadge status="cancelled" />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2">Avec point :</Typography>
          <StatusBadge status="pending" showDot />
          <StatusBadge status="delivered" showDot />
          <StatusBadge status="inProgress" showDot />
        </Box>
      </Box>

      {/* Customer Type Badges */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Customer Type Badges
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <CustomerTypeBadge type="INSTITUTION" />
          <CustomerTypeBadge type="COMPANY" />
          <CustomerTypeBadge type="INDIVIDUAL" />
        </Box>
      </Box>

      {/* Input Fields */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Input Fields
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InputField
              label="Email"
              type="email"
              placeholder="exemple@domaine.com"
              icon={<Email />}
              iconPosition="start"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputField
              label="Mot de passe"
              type="password"
              icon={<Lock />}
              iconPosition="start"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputField
              label="Montant"
              type="number"
              icon={<Euro />}
              iconPosition="end"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputField
              label="Erreur"
              error
              helperText="Ce champ est requis"
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      {/* Stat Cards */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Stat Cards
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Commandes Totales"
              value={124}
              trend={{ value: 12, label: "vs hier", isPositive: true }}
              icon={<Receipt />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="En Attente"
              value={15}
              highlighted
              icon={<HourglassTop />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Livraisons"
              value={8}
              trend={{ value: 5, label: "vs moyenne", isPositive: true }}
              icon={<LocalShipping />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Recette du Jour"
              value="3,450€"
              trend={{ value: 8, label: "vs semaine dern.", isPositive: true }}
              icon={<Euro />}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Loading Spinner */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Loading Spinner
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <LoadingSpinner message="Chargement..." />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <LoadingSpinner size={60} />
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Empty State */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Empty State
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Card>
          <EmptyState
            title="Aucune commande"
            description="Créez votre première commande pour commencer"
            action={{
              label: "Nouvelle commande",
              onClick: () => console.log("Create order"),
            }}
          />
        </Card>
      </Box>

      {/* Error Message */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h2" gutterBottom>
          Error Message
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ErrorMessage message="Une erreur s'est produite lors du chargement" />
          </Grid>
          <Grid item xs={12} md={6}>
            <ErrorMessage
              title="Erreur de connexion"
              message="Impossible de se connecter au serveur"
              onRetry={() => console.log("Retry")}
              retryLabel="Réessayer"
            />
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
